// Report screen (Screen 6) — PSD §10.2. Document column + rail + 4 accordions.

import { FileText } from 'lucide-react';
import { useActiveValuation, useResult, useAuditEvents, useStore } from '../store/selectors';
import { ReportDocument } from '../components/report/ReportDocument';
import { ReportSummary } from '../components/report/ReportSummary';
import { DisclaimerPanel } from '../components/report/DisclaimerPanel';
import { SafeHarborGuidance } from '../components/report/SafeHarborGuidance';
import { AuditTrail } from '../components/report/AuditTrail';
import { ExportRail } from '../components/report/ExportRail';
import { SignOffPanel } from '../components/report/SignOffPanel';
import { SafeHarborStatus } from '../components/report/SafeHarborStatus';
import { AscAlignment } from '../components/report/AscAlignment';
import { Accordion } from '../components/results/Accordion';
import { EmptyState } from '../components/common/EmptyState';
import { LoadDemoButton } from '../components/common/LoadDemoButton';
import { ErrorState } from '../components/common/ErrorState';

export function Report() {
  const active = useActiveValuation();
  const result = useResult(active?.valuationId ?? null);
  const events = useAuditEvents(active?.valuationId ?? null);
  const submitSignOff = useStore((s) => s.submitSignOff);
  const navigate = useStore((s) => s.navigate);

  if (!active) {
    return (
      <div>
        <h1 className="screen-title">Report</h1>
        <EmptyState
          icon={<FileText size={32} strokeWidth={1.5} />}
          message="No active valuation."
          hint="The 409A report — headline FMV, allocation, audit trail, and sign-off — is generated from a converged valuation, and none is selected as active."
          action={<LoadDemoButton />}
        />
      </div>
    );
  }

  if (!result) {
    return (
      <div>
        <h1 className="screen-title">Report</h1>
        <EmptyState
          icon={<FileText size={32} strokeWidth={1.5} />}
          message="Run the valuation to generate a report."
          hint="A defensible 409A report can only be issued once the solver has converged and allocations reconcile — the active valuation hasn't been run yet."
          action={
            <button type="button" className="btn btn-primary" onClick={() => navigate('builder')}>
              Go to Builder
            </button>
          }
        />
      </div>
    );
  }

  // Guard: do not render an authoritative 409A deliverable from a non-converged
  // or unreconciled run — the underlying FMV is not reliable (mirrors Results.tsx).
  if (!result.solver.converged || !result.reconciled) {
    return (
      <div>
        <h1 className="screen-title">Report</h1>
        <ErrorState
          message="The valuation did not converge or did not reconcile — the report cannot be generated. Review inputs and re-run."
          action={
            <button type="button" className="btn btn-secondary" onClick={() => navigate('builder')}>
              Review inputs &amp; re-run
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <h1 className="screen-title">Report</h1>
      <div className="report-layout">
        <div>
          <ReportDocument result={result} />
          <div className="mt-4">
            <ReportSummary result={result} />
          </div>
          <div className="mt-4">
            <DisclaimerPanel />
          </div>
          <div className="mt-4">
            <Accordion title="Assumption / Input Audit Trail" badge={<span className="badge badge-info">F8.4</span>}>
              <AuditTrail events={events} />
            </Accordion>
            <Accordion title="Assumption Memo" badge={<span className="badge badge-info">F8.6</span>}>
              <pre className="memo-preview" style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-1)' }}>
                {`Assumption memo is available via the Export Rail →\nAssumption Memo (TXT) download.\n\nIt includes:\n  • Compliance parameters\n  • Equity value anchor\n  • Allocation assumptions (volatility, TTL, risk-free, dividend)\n  • Discount inputs (DLOM, minority)\n  • Solver diagnostics\n  • Headline outputs\n  • Validation issues`}
              </pre>
            </Accordion>
            <Accordion title="ASC 718 / ASC 820 Alignment" badge={<span className="badge badge-info">F9.7</span>}>
              <AscAlignment result={result} />
            </Accordion>
            <Accordion title="Safe-Harbor Guidance" badge={<span className="badge badge-info">F9.1</span>}>
              <SafeHarborGuidance />
            </Accordion>
          </div>
        </div>
        <aside className="stack-4">
          <ExportRail result={result} />
          <SignOffPanel
            signOff={active.signOff}
            onSubmit={(annotations, approved) =>
              active.valuationId && submitSignOff(active.valuationId, annotations, approved)
            }
          />
          <div className="card">
            <div className="card-title">Safe-Harbor Status</div>
            <SafeHarborStatus
              safeHarbor={active.inputs.compliance.safeHarbor}
              windowDays={result.headline.safeHarborWindowDays}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
